import { CENTS_PER_KM_CAP_KM, centsPerKmRate } from "@stb/jurisdiction-au"
import {
  Car,
  FileSpreadsheet,
  Gauge,
  MapPinned,
  Pencil,
  Route,
  Smartphone,
  Timer,
} from "lucide-react"

import { TRIAL_DAYS } from "@/brand/plan-numbers"

import { PhoneTripMock, TripIllustration } from "../../components/home/home-art"
import { displayYear, formatCheckedOn } from "../../components/home/home-content"
import { LogbookIllustration, MethodsMock } from "../../components/landing/product-art"
import { type ProductPageContent, withIcons } from "../../components/landing/product-page"
import { MILEAGE_EXAMPLE as ex } from "./example-numbers"

/** Copy source: docs/design/landing/copy-products.md (/mileage-tracker). Only features that ship today. */
const rate = centsPerKmRate(ex.financialYear)
const year = displayYear(ex.financialYear)
const checked = formatCheckedOn(rate.checkedOn)
const cap = CENTS_PER_KM_CAP_KM.toLocaleString("en-AU")

export const MILEAGE_SEO = {
  title: "Car logbook and mileage tracker for sole traders",
  description:
    "GPS trips on your phone, a reason for each one, both ATO methods compared and a CSV trip log. Estimates only. Free to start.",
}

const COPY: ProductPageContent = {
  path: "/mileage-tracker",
  name: "Mileage tracker",
  tag: "Mileage tracker",
  reviewed: "28 October 2026",
  h1: "A car logbook and mileage tracker that does the paperwork for you",
  lead: "Your phone records work trips with GPS. You add a reason in seconds. At tax time you compare the two ATO methods and download your trip log.",
  answer: {
    text: `You can claim work use of a car with the cents per kilometre method (up to ${cap} business km per car) or the logbook method, which uses a 12-week logbook to work out the business share of your actual costs. For ${year} the cents per kilometre rate is ${rate.cents} cents.`,
    sources: [
      { label: "ATO, cents per kilometre method (individuals)", href: rate.sourceUrl },
      {
        label: "business page",
        href: "https://www.ato.gov.au/businesses-and-organisations/income-deductions-and-concessions/income-and-deductions-for-business/deductions/deductions-for-motor-vehicle-expenses/cents-per-kilometre-method",
      },
    ],
    financialYear: year,
    checked,
  },
  cta: { label: "Start free trial", href: "/login" },
  secondaryCta: { label: "See pricing", href: "/pricing" },
  heroNote: "iPhone and Android. Deduction figures are estimates only.",
  stats: [
    { value: `${rate.cents} c`, label: `per km in ${year}` },
    { value: `${cap} km`, label: "cents per km cap, per car" },
    { value: "CSV", label: "trip log download" },
    { value: "2 methods", label: "compared side by side" },
  ],
  stepsTitle: "From drive to report in three steps",
  steps: [
    {
      title: "Drive",
      body: "Trips start and stop on their own. They save on your phone first and sync later.",
    },
    { title: "Review", body: "Add a reason and mark each trip business or private on the web." },
    {
      title: "Compare and download",
      body: "See both methods side by side, then download your trip log as CSV for your accountant.",
    },
  ],
  example: {
    title: "Compare methods",
    caption: "Compare methods: example year",
    columns: ["Example year", "Cents per km", "Logbook"],
    rows: [
      { header: "Business km", cells: [ex.businessKm, ex.businessKm] },
      { header: "Estimate", cells: [ex.centsPerKm, ex.logbook] },
    ],
    note: `Example only: ${ex.businessKm} business km, ${ex.businessPercent}% business use, ${ex.carCosts} of car costs. At ${ex.businessPercent}% business use and ${ex.businessKm} km, the logbook method gives more once yearly costs pass about ${ex.breakEven}. Estimate only.`,
  },
  included: [
    {
      title: "Automatic trips",
      body: "The phone detects drives and records the route with GPS, then saves the trip.",
    },
    {
      title: "Trip reasons, when you need them",
      body: "Add a purpose in seconds and mark each trip business or private. Do several at once on the web.",
    },
    {
      title: "Both methods compared",
      body: "Estimates for cents per km and the logbook method, with the break-even point.",
    },
    {
      title: "Odometer readings",
      body: "Keep odometer readings for each vehicle next to your trips.",
    },
    {
      title: "CSV download",
      body: "Download your trip log as CSV, or export all your data as a zip.",
    },
  ],
  limits: {
    title: "Your phone is the recorder",
    body: (
      <>
        The website cannot track trips in the background; use the app to record and the website to
        review and report. Kestly gives estimates, not tax advice. Motorcycles, and vehicles with a
        carrying capacity of 1 tonne or more or 9 or more passengers, cannot use these two methods.
        Source:{" "}
        <a
          href={rate.sourceUrl}
          className="focus-visible:ring-ring rounded-sm underline underline-offset-2 outline-none focus-visible:ring-2"
        >
          ATO, expenses for a car you own or lease
        </a>
        , checked {checked}.
      </>
    ),
  },
  who: [
    {
      title: "Sole traders and contractors",
      body: "Tradies, cleaners and consultants who drive between jobs and claim car costs.",
    },
    {
      title: "Employees who claim car costs",
      body: "No ABN needed for the mileage plan. Keep your trips and reasons in one place.",
    },
    {
      title: "Rideshare and delivery drivers",
      body: "Keep trips for business use separate from private driving.",
    },
  ],
  trust: {
    title: "Your trips stay on record",
    body: "Trips save on your phone first and sync when you are back online. Download your trip log as CSV whenever you need it. See where your data lives on the trust page.",
  },
  plan: {
    rows: ["trips", "vehicles"],
    extraRows: [{ header: "Both methods compared", cells: ["Yes", "Yes", "Yes"] }],
  },
  faqTitle: "Mileage tracker questions",
  faq: [
    {
      question: "Do I need a logbook?",
      answer: (
        <>
          Not for the cents per kilometre method, which covers up to {cap} business km per car. The
          logbook method needs a logbook kept for at least 12 continuous weeks. Source:{" "}
          <a
            href={rate.sourceUrl}
            className="focus-visible:ring-ring rounded-sm underline underline-offset-2 outline-none focus-visible:ring-2"
          >
            ATO, cents per kilometre method
          </a>
          , FY {year}, checked {checked}.
        </>
      ),
    },
    {
      question: "Can I use it without an ABN?",
      answer: "Yes. The mileage plan has no invoices or books, so it does not ask for an ABN.",
    },
    {
      question: "Does it work without signal?",
      answer: "Yes. Trips are stored on your phone and sync when you are back online.",
    },
  ],
  related: [
    { label: "Logbook method: when it pays off", href: "/logbook-method" },
    { label: "Cents per km rate and calculator", href: "/cents-per-km-calculator" },
    { label: "Receipts and expenses", href: "/receipt-scanner" },
    { label: "Invoicing", href: "/invoicing" },
    { label: "Rideshare and delivery drivers: ABN, GST, BAS", href: "/rideshare-drivers" },
    { label: "Kestly vs Driversnote", href: "/compare/driversnote-alternative" },
    { label: "Spreadsheet logbook or app?", href: "/compare/spreadsheet-logbook" },
    { label: "How it works", href: "/how-it-works" },
    { label: "Pricing", href: "/pricing" },
    { label: "Where your data lives", href: "/trust" },
  ],
  band: {
    title: "Start your mileage logbook today",
    description: `Choose just track my driving and try the Logbook plan free for ${TRIAL_DAYS} days. No card needed.`,
  },
}

const STEP_ART = [
  <TripIllustration key="trip" />,
  <LogbookIllustration key="logbook" />,
  <MethodsMock key="methods" />,
]

/** Copy above; icons and pictures (T854) are presentation only. */
export const MILEAGE_TRACKER: ProductPageContent = {
  ...COPY,
  heroCard: <PhoneTripMock />,
  stats: withIcons(COPY.stats, [Gauge, Car, Timer, FileSpreadsheet]),
  steps: COPY.steps.map((step, i) => ({ ...step, art: STEP_ART[i], artOnMobile: i === 2 })),
  included: withIcons(COPY.included, [Route, Pencil, Gauge, MapPinned, FileSpreadsheet]),
  limits: { ...COPY.limits, icon: Smartphone },
  who: withIcons(COPY.who, [Car, Pencil, Route]),
}
